<template>
  <div>
    <img
        :src="icon"
        class="account-verification__step__icon"
        :class="additionalCustomIconStyles">

    <h1 class="account-verification__step__title">
      {{ title }}
    </h1>

    <p class="account-verification__step__description">
      {{ description }}
    </p>

    <p class="account-verification__step__description account-verification__step__data">
      {{ confirmationData }}
    </p>

    <div
        v-if="!isProcessing"
        class="account-verification__step__buttons-container account-verification__step__buttons-container__ring">
      <el-button
          plain
          type="primary"
          class="account-verification__step__button account-verification__step__button-white"
          :class="backButtonVisibility"
          @click="notifyBack">
        {{ backButtonText }}
      </el-button>
      <el-button
          type="primary"
          class="account-verification__step__button"
          @click="sendNotification">
        {{ processButtonText }}
      </el-button>
    </div>

    <processing-info
        v-else-if="isProcessing"
        :message="processingMessage"
        :info-type="processingInfoType"
        class="email-confirmation__processing-info"
        :style="additionalProcessingInfoStyles" />
  </div>
</template>

<script>
import ProcessingInfo from "./ProcessingInfo";
import axios from "axios";
import { createNamespacedHelpers as module } from "vuex";

const { mapGetters: accountGetters } = module("db/account");

export default {
  name: "DataConfirmationStep",
  components: { ProcessingInfo },
  emits: ["nextStepRequested", "previousStepRequested", "connectionError"],
  inject: ["VERIFICATION_METHODS"],
  props: {
    verificationMethodType: {
      type: String,
      required: true,
    },
    isResendCodeRequest: {
      type: Boolean,
      required: false,
      default: false,
    },
    icon: {
      type: String,
      required: true,
    },
    allowedVerificationMethods: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isProcessing: false,
      processingMessage: null,
      processingInfoType: "loading",
      title: null,
      description: null,
      confirmationData: null,
      backButtonText: null,
      processButtonText: null,
    };
  },
  computed: {
    ...accountGetters(["customerId"]),
    additionalCustomIconStyles() {
      if (
        this.VERIFICATION_METHODS.EMAIL.includes(this.verificationMethodType)
      ) {
        return "account-verification__step__custom-email-icon";
      } else {
        return "";
      }
    },
    additionalProcessingInfoStyles() {
      if (
        this.VERIFICATION_METHODS.EMAIL.includes(this.verificationMethodType)
      ) {
        return "email-confirmation__processing-info";
      } else {
        return "";
      }
    },
    backButtonVisibility() {
      if (
        this.allowedVerificationMethods === undefined ||
        this.allowedVerificationMethods === null ||
        this.allowedVerificationMethods.length === 1
      ) {
        return "visibility__hidden";
      }

      return "";
    },
  },
  mounted() {
    axios
      .get(`v3/customers/${this.customerId}/account_verification/data`)
      .then((response) => {
        this.initialize(response.data);
      })
      .catch((error) => {
        console.log(`Error getting user information ${JSON.stringify(error)}`);
      });
  },
  methods: {
    initialize(userData) {
      this.backButtonText = "Try Different Method";

      if (this.VERIFICATION_METHODS.SMS.includes(this.verificationMethodType)) {
        this.title = "Make sure this phone number is correct.";
        this.confirmationData = userData.phone;
      } else if (
        this.VERIFICATION_METHODS.EMAIL.includes(this.verificationMethodType)
      ) {
        this.title = "Make sure this email is correct.";
        this.confirmationData = userData.email;
      }

      this.initializeRing(userData);

      if (this.isResendCodeRequest) {
        this.sendNotification();
      }
    },
    initializeRing() {
      if (this.VERIFICATION_METHODS.SMS.includes(this.verificationMethodType)) {
        this.description =
          "We’ll send a passcode to the phone number below, then ask you to confirm it.";
        this.processButtonText = "Send Passcode";
        this.processingMessage = "Sending Passcode...";
      } else if (
        this.VERIFICATION_METHODS.EMAIL.includes(this.verificationMethodType)
      ) {
        this.description =
          "We’ll send a passcode to the email below, then ask you to confirm it.";
        this.processButtonText = "Send Link";
        this.processingMessage = "Sending Email...";
      }
    },
    sendNotification() {
      this.isProcessing = true;
      this.getSendNotificationRequest()
        .then((response) => {
          this.processingInfoType = "success";
          this.processingMessage = "Success !";

          setTimeout(() => {
            this.notifyContinue();
          }, 1000);
        })
        .catch((error) => {
          this.$emit("connectionError", true);
        });
    },
    getSendNotificationRequest() {
      const channel = {
        channel: this.verificationMethodType,
      };

      const timeout = { timeout: 20 * 1000 };
      return axios.post(
        `/v3/customers/${this.customerId}/account_verification/generate_code`,
        channel,
        timeout
      );
    },
    notifyContinue() {
      this.$emit("nextStepRequested", true);
    },
    notifyBack() {
      this.$emit("previousStepRequested", true);
    },
  },
};
</script>

<style scoped>
.account-verification__step__buttons-container {
  display: inline-block;
  width: 100%;
  text-align: center;
}

.account-verification__step__buttons-container__ring {
  margin-top: 88px;
}

/* This is set on additionalProcessingInfoStyles computed property */
.email-confirmation__processing-info {
  margin-top: 78px;
}

/* This is set by additionalCustomIconStyles computed property */
.account-verification__step__custom-email-icon {
  width: 40px;
  height: 28px;
}

.visibility__hidden {
  visibility: hidden;
}
</style>
